import { CommonModule } from '@angular/common';
import { NgModule } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatMenuModule } from '@angular/material/menu';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTooltipModule } from '@angular/material/tooltip';
import { CodemirrorModule } from '@ctrl/ngx-codemirror';
import { PopoverModule } from 'ngx-bootstrap/popover';

import { ComponentsModule } from '../components/components.module';
import { PresentationsModule } from '../presentations/presentations.module';
import { RvSortableModule } from '../rv-sortable/rv-sortable.module';
import { SchedulesModule } from '../schedules/schedules.module';
import { SharedModule } from '../shared/shared.module';

import { ContextualToolbarComponent } from './components/contextual-toolbar/contextual-toolbar.component';
import { TemplateAttributeComponentsComponent } from './components/template-attribute-components/template-attribute-components.component';
import { TemplateAttributeComponentsMenuDropdownComponent } from './components/template-attribute-components-menu-dropdown/template-attribute-components-menu-dropdown.component';
import { TemplateAttributeComponentsMenuInlineComponent } from './components/template-attribute-components-menu-inline/template-attribute-components-menu-inline.component';
import { TemplateAttributeEditorComponent } from './components/template-attribute-editor/template-attribute-editor.component';
import { TemplateAttributeListComponent } from './components/template-attribute-list/template-attribute-list.component';
import { TemplateComponentComponent } from './components/template-component/template-component.component';
import { TemplateEditorFooterComponent } from './components/template-editor-footer/template-editor-footer.component';
import { TemplateEditorToolbarComponent } from './components/template-editor-toolbar/template-editor-toolbar.component';
import { TemplateEditorComponent } from './components/template-editor/template-editor.component';
import { TemplateShareComponent } from './template-components/template-share/template-share.component';

import { UIRouterModule } from '@uirouter/angular';
import { BasicUploaderComponent } from './components/basic-uploader/basic-uploader.component';
import { EmptyFileListComponent } from './components/empty-file-list/empty-file-list.component';
import { FileEntryComponent } from './components/file-entry/file-entry.component';
import { CredentialsModalComponent } from './components/credentials-modal/credentials-modal.component';
import { OnedriveFilePickerComponent } from './components/onedrive-file-picker/onedrive-file-picker.component';
import { TenantNameModalComponent } from './components/tenant-name-modal/tenant-name-modal.component';
import { EncodeLinkPipe } from './pipes/encode-link.pipe';
import { BrandingColorsComponent } from './template-components/branding-colors/branding-colors.component';
import { BrandingComponent } from './template-components/branding/branding.component';
import { CalendarComponent } from './template-components/calendar/calendar.component';
import { CanvaConnectComponent } from './template-components/canva-connect/canva-connect.component';
import { ColorsComponent } from './template-components/colors/colors.component';
import { CounterComponent } from './template-components/counter/counter.component';
import { CustomFontSelectorComponent } from './template-components/custom-font-selector/custom-font-selector.component';
import { DataTableComponent } from './template-components/data-table/data-table.component';
import { DataTableContentEditorComponent } from './template-components/data-table-common/data-table-content-editor.component';
import { DocumentComponent } from './template-components/document/document.component';
import { DomoComponent } from './template-components/domo/domo.component';
import { FinancialComponent } from './template-components/financial/financial.component';
import { FolderComponent } from './template-components/folder/folder.component';
import { HtmlComponent } from './template-components/html/html.component';
import { IconComponent } from './template-components/icon/icon.component';
import { ImageComponent } from './template-components/image/image.component';
import { LayoutDateComponent } from './template-components/layout-date/layout-date.component';
import { LayoutEmsComponent } from './template-components/layout-ems/layout-ems.component';
import { LayoutCalendarComponent } from './template-components/layout-calendar/layout-calendar.component';
import { LayoutRssComponent } from './template-components/layout-rss/layout-rss.component';
import { LayoutCounterComponent } from './template-components/layout-counter/layout-counter.component';
import { LayoutTableComponent } from './template-components/layout-table/layout-table.component';
import { LayoutTimeComponent } from './template-components/layout-time/layout-time.component';
import { LayoutWeatherComponent } from './template-components/layout-weather/layout-weather.component';
import { MediaSelectorComponent } from './template-components/media-selector/media-selector.component';
import { PlaylistComponent } from './template-components/playlist/playlist.component';
import { PowerBIComponent } from './template-components/powerbi/powerbi.component';
import { PresentationSelectorComponent } from './template-components/presentation-selector/presentation-selector.component';
import { RssComponent } from './template-components/rss/rss.component';
import { SchedulesComponent } from './template-components/schedules/schedules.component';
import { ScreenSharingComponent } from './template-components/screen-sharing/screen-sharing.component';
import { SlidesComponent } from './template-components/slides/slides.component';
import { SocialMediaFollowersComponent } from './template-components/social-media-followers/social-media-followers.component';
import { SocialMediaPostsComponent } from './template-components/social-media-posts/social-media-posts.component';
import { StorageSelectorComponent } from './template-components/storage-selector/storage-selector.component';
import { TextComponent } from './template-components/text/text.component';
import { TextAiComponent } from './template-components/text-ai/text-ai.component';
import { ImageAiComponent } from './template-components/image-ai/image-ai.component';
import { LayoutAiComponent } from './template-components/layout-ai/layout-ai.component';
import { TimeDateComponent } from './template-components/time-date/time-date.component';
import { TwitterComponent } from './template-components/twitter/twitter.component';
import { VideoStreamingComponent } from './template-components/video-streaming/video-streaming.component';
import { VideoComponent } from './template-components/video/video.component';
import { WeatherComponent } from './template-components/weather/weather.component';
import { WebPageComponent } from './template-components/web-page/web-page.component';
import { YouTubeComponent } from './template-components/youtube/youtube.component';
import { routes } from './template-editor.routes';
import { ViewportComponent } from './viewport/viewport.component';

import { A11yModule } from '@angular/cdk/a11y';
import { OverlayModule } from '@angular/cdk/overlay';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { MatSliderModule } from '@angular/material/slider';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTimepickerModule } from '@angular/material/timepicker';
import { DialogHeaderComponent } from '../components/modals/dialog-header/dialog-header.component';
import { ArrowKeyNavigationModule } from '../components/arrow-key-navigation/arrow-key-navigation.module';
import { FolderPathComponent } from '../components/folders/folder-path/folder-path.component';
import { SearchFilterComponent } from '../components/search-filter/search-filter.component';
import { StatusBadgeComponent } from '../components/status-badge/status-badge.component';
import { PurchaseModule } from '../purchase/purchase.module';
import { StorageModule } from '../storage/storage.module';
import { FilenamePipe } from './pipes/filename.pipe';
import { MultiSelectDropdownComponent } from './components/multi-select-dropdown/multi-select-dropdown.component';
import { MultiSelectPillsComponent } from './components/multi-select-pills/multi-select-pills.component';
import { RvBadgeComponent } from '../components/rv-badge/rv-badge.component';
import { PlaylistItemSelectorComponent } from './template-components/playlist-item-selector/playlist-item-selector.component';
import { StockLibraryComponent } from './template-components/stock-library/stock-library.component';
import { FocusOutlineDirective } from '../accessibility/directives/focus-outline/focus-outline.directive';
import { PresentationOrientationSelectorComponent } from './components/presentation-orientation-selector/presentation-orientation-selector.component';
import { ActionLinkDirective } from '../accessibility/directives/action-link/action-link.directive';

@NgModule({
  imports: [
    ActionLinkDirective,
    CommonModule,
    UIRouterModule.forChild(routes),
    ComponentsModule,
    RvSortableModule,
    SharedModule,
    FormsModule,
    CodemirrorModule,
    PopoverModule,
    PurchaseModule,
    SchedulesModule,
    StorageModule,
    PresentationsModule,
    A11yModule,
    OverlayModule,
    MatMenuModule,
    MatTooltipModule,
    RvBadgeComponent,
    MatButtonToggleModule,
    MatSlideToggleModule,
    MatCheckboxModule,
    MatRadioModule,
    MatFormFieldModule,
    MatInputModule,
    MatDatepickerModule,
    MatTimepickerModule,
    MatButtonModule,
    MatNativeDateModule,
    MatIconModule,
    MatSelectModule,
    MatSliderModule,
    MatDialogModule,
    MatProgressBarModule,
    MatProgressSpinnerModule,
    StatusBadgeComponent,
    DialogHeaderComponent,
    FolderPathComponent,
    SearchFilterComponent,
    ArrowKeyNavigationModule,
    FocusOutlineDirective,
    PresentationOrientationSelectorComponent,
    TemplateEditorToolbarComponent,
  ],
  declarations: [
    TemplateEditorFooterComponent,
    TemplateEditorComponent,
    TemplateAttributeEditorComponent,
    TemplateAttributeListComponent,
    TemplateAttributeComponentsComponent,
    TemplateAttributeComponentsMenuDropdownComponent,
    TemplateAttributeComponentsMenuInlineComponent,
    ContextualToolbarComponent,
    TemplateComponentComponent,
    EncodeLinkPipe,
    WeatherComponent,
    TimeDateComponent,
    StorageSelectorComponent,
    BrandingComponent,
    BrandingColorsComponent,
    SchedulesComponent,
    FinancialComponent,
    ImageComponent,
    IconComponent,
    SlidesComponent,
    TextComponent,
    TextAiComponent,
    ImageAiComponent,
    LayoutAiComponent,
    VideoComponent,
    RssComponent,
    CounterComponent,
    ColorsComponent,
    TwitterComponent,
    PresentationSelectorComponent,
    PlaylistComponent,
    HtmlComponent,
    EmptyFileListComponent,
    FileEntryComponent,
    BasicUploaderComponent,
    ViewportComponent,
    CanvaConnectComponent,
    FolderComponent,
    MediaSelectorComponent,
    TemplateShareComponent,
    YouTubeComponent,
    CalendarComponent,
    DataTableComponent,
    DataTableContentEditorComponent,
    WebPageComponent,
    DomoComponent,
    PlaylistItemSelectorComponent,
    StockLibraryComponent,
    PowerBIComponent,
    DocumentComponent,
    OnedriveFilePickerComponent,
    CredentialsModalComponent,
    TenantNameModalComponent,
    SocialMediaPostsComponent,
    SocialMediaFollowersComponent,
    VideoStreamingComponent,
    CustomFontSelectorComponent,
    ScreenSharingComponent,
    LayoutWeatherComponent,
    LayoutTimeComponent,
    LayoutDateComponent,
    LayoutEmsComponent,
    LayoutCalendarComponent,
    LayoutRssComponent,
    LayoutCounterComponent,
    LayoutTableComponent,
    FilenamePipe,
    MultiSelectDropdownComponent,
    MultiSelectPillsComponent,
  ],
})
export class TemplateEditorModule {}
